import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, CardBody, CardHeader, Col, Collapse, DropdownItem, DropdownMenu, DropdownToggle, Row, Spinner, UncontrolledDropdown } from 'reactstrap';
import { useExecutionInfo } from '../hooks/useExecutionInfo';
import { useHighlightJson } from '../hooks/useHighlightsJson';
import { CueItem, useSubtitleJson } from '../hooks/useSubtitleJson';
// import { SummaryResponse, useSummaryJson } from '../hooks/useSummaryJson';
import { useStoreActions } from '../stores/store';
import { ExecutionStatus, SentimentType } from '../types';
import { countSentences } from '../utils/countSentences';
import { countWords } from '../utils/countWords';
import { secondsToTimeString } from '../utils/secondsToTimeString';
import { useStoreState as useSubtitleStoreState, useStoreActions as useSubtitleStoreActions } from '../stores/store'
import TextModal from './TextModal';
import axios from 'axios';

const ExecutionInfoSubtitles: React.FC<{ id: string }> = ({ id }) => {
  const { data, isLoading, error } = useExecutionInfo(id)
  const { data: subtitleJson } = useSubtitleJson(data?.subtitlesJsonUrl)
  // const { data: summary } = useSummaryJson(data?.summaryFileUrl)
  const { data: highlight } = useHighlightJson(data?.highlightFileUrl)
  const [keySentenceModalOpen, setKeySentenceModalOpen] = useState(false)
  const toggle = () => setKeySentenceModalOpen(!keySentenceModalOpen)

  const searchTerm = useSubtitleStoreState(s => s.searchTerm)
  const updateSearchTerm = useSubtitleStoreActions(s => s.updateSearchTerm)

  const [collapsed, setCollapsed] = useState(true)
  const [searchString, setSearchString] = useState('')
  // const [prevSearchedString, setPrevSearchedString] = useState('')
  const [searched, setSearched] = useState(false)
  const [filteredData, setFilteredData] = useState<CueItem[]>([])
  const [showSentimentSentences, setShowSentimentSentences] = useState(false)
  const [filterSentiment, setFilterSentiment] = useState<SentimentType>()
  // const [summaryModalOpen, setSummaryModalOpen] = useState(false)
  // const [selectedSummary, setSelectedSummary] = useState<keyof SummaryResponse>()
  const seekTo = useStoreActions(actions => actions.seekTo)
  const token = useMemo(() => localStorage.getItem('token'), [])
  const [highlightedText, setHighlightedText] = useState('');

  interface HighlightData {
    results: HighlightResult[];
    // other properties of highlight
  }
  type HighlightResult = {
    count: number;
    rank: number;
    text: string;
    timestamps: {
      start: number;
      end: number;
    }[];
    from?: string;
  };

  const initialCueItems = useMemo<CueItem[]>(() => {
    if (subtitleJson && subtitleJson.length > 0) {
      return subtitleJson.filter(d => d.type === 'cue') as CueItem[]
    }
    return []
  }, [subtitleJson])

  const subtitleText = useMemo(() => {
    return initialCueItems.reduce((text, curr) => {
      return `${text} ${curr.data.text}`
    }, '')
  }, [initialCueItems])

  useEffect(() => {
    if (highlight && "results" in highlight) {
      const highlightResults = (highlight as HighlightData).results;
      if (highlightResults[0]?.from === "earnings"){
      setHighlightedText(highlightResults[0].text);
      }
    } else {
      setHighlightedText(subtitleText);
    }
  }, [highlight, subtitleText]);

  const highlightedSubText = useMemo(() => {
    let text = subtitleText;
    if (!highlight) return text;

    if (highlight && "results" in highlight) {
      // console.log("highlight ---", highlight);
      const highlightResults = (highlight as HighlightData).results;
      // console.log("highlightResults ---", highlightResults);
      if (highlightResults[0]?.from === "earnings"){

        return highlightResults[0].text;
      }
    }

    for (const { text: h } of highlight) {
      text = text.replace(
        new RegExp(h, "ig"),
        `<b style="color: orange;">${h}</b>`
      );
    }
    return text;
  }, [subtitleText, highlight]);

  const highlightedSentimentSentences = useMemo(() => {
    if (!data || !data.sentiments || data.sentiments.length === 0) return ''
    let text = ''

    for (const [sentence, sentiment] of data.sentiments) {
      if (filterSentiment && sentiment !== filterSentiment) continue

      if (sentiment === 'Neutral') {
        if (filterSentiment) {
          text += `<li style="color: black;">${sentence.trim()}</li>`
        } else {
          text += `<span style="color: black;">${sentence.trim()}</span>`
        }
      } else if (sentiment === 'Positive') {
        if (filterSentiment) {
          text += `<li style="color: green;">${sentence.trim()}</li>`
        } else {
          text += `<span style="color: green;">${sentence.trim()}</span>`
        }
      } else {
        if (filterSentiment) {
          text += `<li style="color: red;">${sentence.trim()}</li>`
        } else {
          text += `<span style="color: red;">${sentence.trim()}</span>`
        }
      }
      if (!filterSentiment) {
        text += ' '
      }
    }
    return filterSentiment ? `<ul>${text.trim()}</ul>` : text.trim()
  }, [data, filterSentiment])

  useEffect(() => {
    if (initialCueItems.length > 0) {
      setFilteredData(initialCueItems)
    }
  }, [initialCueItems])

  useEffect(() => {
    if (!searchTerm) return;
    if (collapsed) {
      setCollapsed(false);
    }
    setSearchString(searchTerm);
    setSearched(true);

    const isEarningsCall =
      data?.execution.status === ExecutionStatus.completed &&
      data?.execution.confName.includes("Earnings call");

    if (isEarningsCall && highlight && "results" in highlight) {
      const highlightResults = (highlight as HighlightData).results;
      if (highlightResults[0]?.from === "earnings") {
        const regex = new RegExp(`.*${searchTerm}.*`, "ig");
        const matches = highlightResults[0].text.match(regex);
    setFilteredData(
      matches
        ? matches.map((match) => ({
            type: "cue",
            data: {
              text: match,
              start: highlightResults[0].text.indexOf(match), // Use index as a proxy for start time
              end: highlightResults[0].text.indexOf(match) + match.length,
            },
          }))
        : []
    );
      }
    } else {
      setFilteredData(
        initialCueItems.filter((c) => {
          const regex = new RegExp(`.*${searchTerm}.*`, "ig");
          return regex.test(c.data.text);
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const goToTopAndSeek = (ms: number) => {
    window.scrollTo(0, 0)
    seekTo(ms)
  }

  const clearSearch = () => {
    setSearched(false)
    updateSearchTerm('')
    // setPrevSearchedString('')
    setFilteredData(initialCueItems)
  }

  const handleSearch = () => {
    if (!searchString) {
      clearSearch()
      return
    }
    if (collapsed) {
      setCollapsed(false)
    }
    updateSearchTerm(searchString)
    // setSearched(true)
    // setPrevSearchedString(searchString)
    // setFilteredData(initialCueItems.filter(c => {
    //   const regex = new RegExp(`.*${searchString}.*`, 'ig')
    //   return regex.test(c.data.text)
    // }))
  }

  // returns subtitle text with a few lines before and after the main text
  const subtitleTextWithNeighbors = useCallback((text: string, maxNeighbors = 3) => {
    const currentIndex = initialCueItems.findIndex(c => c.data.text === text)
    if (currentIndex < 0) {
      return ''
    }
    let preText = ''
    let postText = ''
    let outText = ''

    if (currentIndex > 0) {
      const minIndex = Math.max(0, currentIndex - maxNeighbors)
      for (let i = minIndex; i < currentIndex; i++) {
        preText += `${initialCueItems[i].data.text} `
      }
    }

    if (preText.length > 0) {
      outText = `${preText} `
    }
    outText += text

    if (currentIndex < initialCueItems.length - 1) {
      const maxIndex = Math.min(initialCueItems.length - 1, currentIndex + maxNeighbors)

      for (let i = currentIndex + 1; i < maxIndex; i++) {
        postText += `${initialCueItems[i].data.text} `
      }
    }
    if (postText.length > 0) {
      outText += ` ${postText}`
    }
    return outText
  }, [initialCueItems])


  const highlightTextWithNeighbors = useCallback((text: string, maxNeighbors = 100) => {
    if (!highlightedText) return text;
    
    const textIndex = highlightedText.indexOf(text);
    if (textIndex === -1) return text;

    const startIndex = Math.max(0, textIndex - maxNeighbors);
    const endIndex = Math.min(highlightedText.length, textIndex + text.length + maxNeighbors);

    let result = highlightedText.slice(startIndex, endIndex);

    // Add ellipsis if we're not at the start or end of the full text
    if (startIndex > 0) result = '... ' + result;
    if (endIndex < highlightedText.length) result += ' ...';

    return result;
  }, [highlightedText]);


  const handleHighlightSentiments = () => {
    setFilterSentiment(undefined)
    setShowSentimentSentences(!showSentimentSentences)
  }

  
  const handleEarningsSummaryReportDownload = async () => {
    try {
      if (data?.earningsSummaryReportUrl) {
        console.log("Opening earnings summary:", data.earningsSummaryReportUrl);
        window.open(data.earningsSummaryReportUrl, "_blank");
      } else {
        console.error("No URL available for the earnings summary report");
      }
    } catch (error) {
      console.error("Error downloading earnings summary:", error);
    }
  };
  

  let content: JSX.Element | null = null

  if (isLoading) {
    content = <Spinner />
  } else if (!data || error) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    )
  } else if(data?.execution.status === ExecutionStatus.completed &&
    data?.execution.confName.includes("Earnings call") ) {
   
    content = (
      <>
        {searched ? (
          <>
            {filteredData.length === 0 && (
              <Alert color="warning">No results found!</Alert>
            )}
            {filteredData.map((f, i) => (
              <div key={i}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: highlightTextWithNeighbors(f.data.text)
                      .replace(
                        f.data.text,
                        `<span style="border: 1px solid darkgray;border-radius: 5px;padding: 0 5px;background: white;">
                          ${f.data.text}
                        </span>`
                      )
                      .replace(
                        new RegExp(searchTerm, 'ig'),
                        `<b style="color: orange; padding: 0; margin: 0;">
                          ${searchTerm}
                        </b>`
                      )
                  }}
                  style={{
                    marginBottom: 0,
                    paddingBottom: 0
                  }}
                />
                {/* <small>Index: {f.data.start}</small> */}
                <br />
                {/* <Button
                  size="sm"
                  color="primary"
                  onClick={() => goToTopAndSeek(f.data.start)}
                >
                  Seek here
                </Button> */}
                <hr />
              </div>
            ))}
          </>
        ) : (
          <p
            dangerouslySetInnerHTML={{
              __html: showSentimentSentences ? highlightedSentimentSentences : highlightedSubText
            }}
          />
        )}
      </>
    );
  } else {
     content = (
      <>
        {
          searched ?
            <>
              {
                filteredData.length === 0 &&
                <Alert color="warning">
                  No results found!
                </Alert>
              }
              {
                filteredData.map((f, i) => (
                  <div
                    key={i}
                  >
                    <p
                      dangerouslySetInnerHTML={{
                        __html: subtitleTextWithNeighbors(f.data.text)
                          .replace(
                            f.data.text,
                            `<span style="border: 1px solid darkgray;border-radius: 5px;padding: 0 5px;background: white;">
                              ${f.data.text}
                            </span>`
                          )
                          .replace(
                            new RegExp(searchTerm, 'ig'),
                            `<b style="color: orange; padding: 0; margin: 0;">
                              ${searchTerm}
                            </b>`
                          )
                      }}
                      style={{
                        marginBottom: 0,
                        paddingBottom: 0
                      }}
                    />
                    <small>Talked at {secondsToTimeString(f.data.start / 1000)}</small>
                    <br />
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => goToTopAndSeek(f.data.start)}
                    >
                      Seek here
                    </Button>
                    <hr />
                  </div>
                ))
              }
            </>
            :
            <>
              <p
                dangerouslySetInnerHTML={{
                  __html: showSentimentSentences ? highlightedSentimentSentences : highlightedSubText
                }}
              >
              </p>
            </>
        }
      </>
    )
  }


  return (
    <>
      <Row>
        <Col className="order-xl-1">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8" sm="3">
                  <h3 className="mb-0">Transcript</h3>
                  <small>
                    <b>
                      {countWords(subtitleText)} words &{" "}
                      {countSentences(subtitleText)} sentences
                    </b>
                  </small>
                  {data?.sentiments && data.sentiments.length > 0 && (
                    <>
                      <br />
                      <small style={{ fontSize: "70%" }}>
                        <b>
                          {
                            data.sentiments.filter((s) => s[1] === "Positive")
                              .length
                          }{" "}
                          Positive,&nbsp;
                          {
                            data.sentiments.filter((s) => s[1] === "Negative")
                              .length
                          }{" "}
                          Negative,&nbsp;
                          {
                            data.sentiments.filter((s) => s[1] === "Neutral")
                              .length
                          }{" "}
                          Neutral
                        </b>
                      </small>
                    </>
                  )}
                </Col>
                <Col xs="8" sm="5">
                  <div className="input-group-alternative input-group">
                    <div
                      className="input-group-prepend"
                      style={{ width: "100%" }}
                    >
                      <span onClick={handleSearch} className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search for anything"
                        id="subt_search"
                        value={searchString}
                        onChange={(e) => {
                          setSearchString(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key.toLowerCase() === "enter") {
                            handleSearch();
                          }
                        }}
                      />
                    </div>
                  </div>
                </Col>
                <Col className="text-right" xs="4">
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => {
                      setCollapsed(!collapsed);
                    }}
                  >
                    {collapsed ? "Expand" : "Collapse"}
                  </Button>
                </Col>
              </Row>
              <Row className="float-right mt-2">
                <Col>
                  {data?.sentiments && data.sentiments.length > 0 && (
                    <>
                      <UncontrolledDropdown className="mx-0" size="sm">
                        <DropdownToggle caret color="primary">
                          {!filterSentiment
                            ? "Filter Sentences"
                            : filterSentiment}
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem
                            onClick={() => {
                              setFilterSentiment("Positive");
                              setShowSentimentSentences(true);
                            }}
                          >
                            Positive
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {
                              setFilterSentiment("Negative");
                              setShowSentimentSentences(true);
                            }}
                          >
                            Negative
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {
                              setFilterSentiment("Neutral");
                              setShowSentimentSentences(true);
                            }}
                          >
                            Neutral
                          </DropdownItem>
                          <DropdownItem
                            onClick={() => {
                              setFilterSentiment(undefined);
                            }}
                          >
                            Clear
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                      <Button
                        size="sm"
                        color="primary"
                        className="mr-2"
                        onClick={handleHighlightSentiments}
                      >
                        {showSentimentSentences && "Undo"} Highlight Sentiments
                      </Button>
                    </>
                  )}
                  {data?.keySentences && data.keySentences.length > 0 && (
                    <>
                      <Button
                        className="mx-0"
                        size="sm"
                        color="primary"
                        onClick={toggle}
                      >
                        Summary
                      </Button>
                      <TextModal
                        header="Key Sentences"
                        text={data.keySentences}
                        open={keySentenceModalOpen}
                        toggle={toggle}
                      />
                    </>
                  )}
                  {data?.execution.status === ExecutionStatus.completed && (
                    <a
                      href={`${process.env.REACT_APP_API_BASE_URL}/execution/${
                        data.execution.id
                      }/report/export?token=${localStorage.getItem("token")}`}
                      className="ml-2"
                      download
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button size="sm" color="primary">
                        Download Report
                      </Button>
                    </a>
                  )}

                  {data?.execution.status === ExecutionStatus.completed &&
                    data?.execution.confName.includes("Earnings call") && (
                      // <a
                      //   href={`${process.env.REACT_APP_API_BASE_URL}/execution/${data.execution.id}/transcriptQnAReport/export?token=${localStorage.getItem('token')}`}
                      //   className="ml-2"
                      //   download
                      //   target="_blank"
                      //   rel="noreferrer"
                      // >
                      //   <Button
                      //     size="sm"
                      //     color="primary"
                      //   >
                      //     Download Earnings Summary
                      //   </Button>
                      // </a>
                      <Button
                        size="sm"
                        color="primary"
                        className="ml-2"
                        onClick={handleEarningsSummaryReportDownload}
                      >
                        Download Earnings Summary
                      </Button>
                    )}

                  {/* {
                    summary &&
                    <>
                      <UncontrolledDropdown size="sm">
                        <DropdownToggle>
                          View Summary
                        </DropdownToggle>
                        <DropdownMenu>
                          {
                            Object.keys(summary).map((s, i) => (
                              <DropdownItem key={i} onClick={() => {
                                setSelectedSummary(s as keyof SummaryResponse)
                                setSummaryModalOpen(true)
                              }}>
                                {s.toUpperCase()} Summary
                              </DropdownItem>
                            ))
                          }
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </>
                  } */}
                  {data?.subtitlesTextUrl && (
                    <a
                      href={data.subtitlesTextUrl + `?token=${token}`}
                      className="mx-2"
                      download
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button size="sm" color="primary">
                        Download Transcript
                      </Button>
                    </a>
                  )}
                  {data?.analysisFileUrl && (
                    <a
                      href={data.analysisFileUrl + `?token=${token}`}
                      download
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button size="sm" color="primary">
                        Download Analysis
                      </Button>
                    </a>
                  )}
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={!collapsed}>
              <CardBody>{content}</CardBody>
             
            </Collapse>
          </Card>
        </Col>
      </Row>
      <br />
      {/* {summary &&
        <TextModal
          header={`${selectedSummary} Summary`}
          text={selectedSummary ? summary[selectedSummary] : ''}
          open={summaryModalOpen}
          toggle={() => setSummaryModalOpen(false)}
        />
      } */}
    </>
  );
}

export default ExecutionInfoSubtitles
